import {
  getProcess,
  resolveLatestProcessName,
} from '../transactions/transaction';

import {
  bookingHasEnded,
  bookingTimes,
  instantFrom,
  txPastListenerNoShowDeadline,
  txPastNoShowDeadline,
} from './sessionClock';
import { isTalkerLateCancelCharge } from './cancelPolicy';
import { transactionWasLeft } from './sessionLeave';
import { transactionIsListenerNoShow, transactionIsTalkerNoShow } from './sessionNotifications';
import { isSupersededEditedBooking } from './editUpcomingBooking';

export const INBOX_SORT_ALL = 'all';
export const INBOX_SORT_UPCOMING = 'upcoming';
export const INBOX_SORT_PAST = 'past';
export const INBOX_SORT_CANCELLED = 'cancelled';
export const INBOX_SORT_BOOKED = 'booked';
export const INBOX_SORT_ACTIVITY = 'activity';

export const INBOX_SORT_KEYS = [
  INBOX_SORT_ALL,
  INBOX_SORT_UPCOMING,
  INBOX_SORT_PAST,
  INBOX_SORT_CANCELLED,
];

export const INBOX_SORT_STORAGE_KEY = 'slInboxSort';

const PAST_STATES = new Set([
  'canceled',
  'cancelled',
  'declined',
  'expired',
  'payment-expired',
  'delivered',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
  'past',
  'no-confirmation',
]);

const COMPLETED_STATES = new Set([
  'delivered',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
  'past',
]);

/**
 * lastTransition names that mean the session was completed / reviewed.
 * Used to query Past (completed) from Sharetribe instead of filtering
 * the first recency page, which is usually all requested/cancelled.
 */
export const COMPLETED_LAST_TRANSITIONS = [
  'transition/charge-after-session',
  'transition/provider-charge-after-session',
  'transition/complete',
  'transition/operator-complete',
  'transition/operator-complete-separate-hold',
  'transition/review-1-by-customer',
  'transition/review-1-by-provider',
  'transition/review-2-by-customer',
  'transition/review-2-by-provider',
  'transition/expire-review-period',
  'transition/expire-customer-review-period',
  'transition/expire-provider-review-period',
];

/**
 * lastTransition names for a still-open talk booking (requested or accepted).
 * Recency page 1 is often 25 recently touched past rows, which buried Edit.
 */
export const UPCOMING_LAST_TRANSITIONS = [
  'transition/confirm-payment',
  'transition/request-payment',
  'transition/remind-provider-accept',
  'transition/accept',
  'transition/operator-accept',
];

const CANCELLED_STATES = new Set(['canceled', 'cancelled']);

const REQUESTED_STATES = new Set(['preauthorized', 'pending-payment', 'inquiry']);

const LEGACY_SORT = {
  createdAt: INBOX_SORT_ALL,
  lastMessageAt: INBOX_SORT_ALL,
  lastTransitionedAt: INBOX_SORT_ALL,
  '-createdAt': INBOX_SORT_ALL,
  '-lastMessageAt': INBOX_SORT_ALL,
  '-lastTransitionedAt': INBOX_SORT_ALL,
  booked: INBOX_SORT_ALL,
  activity: INBOX_SORT_ALL,
};

/**
 * Normalize a URL or stored inbox sort key.
 *
 * @param {string} [value]
 * @returns {string}
 */
export const normalizeInboxSort = value => {
  const raw = typeof value === 'string' ? value : '';
  if (INBOX_SORT_KEYS.includes(raw)) {
    return raw;
  }
  return LEGACY_SORT[raw] || INBOX_SORT_ALL;
};

/**
 * Stored sort choice — same value on Listening and Talking.
 *
 * @returns {string}
 */
export const readInboxSort = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return INBOX_SORT_ALL;
  }
  try {
    return normalizeInboxSort(window.localStorage.getItem(INBOX_SORT_STORAGE_KEY));
  } catch (e) {
    return INBOX_SORT_ALL;
  }
};

/**
 * Persist the inbox sort so a tab switch keeps it.
 *
 * @param {string} value
 */
export const writeInboxSort = value => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(INBOX_SORT_STORAGE_KEY, normalizeInboxSort(value));
  } catch (e) {
    // Safari private mode
  }
};

const processStateOf = tx => {
  try {
    const name = resolveLatestProcessName(tx?.attributes?.processName);
    return getProcess(name)?.getState(tx) || '';
  } catch (e) {
    return '';
  }
};

const msOf = value => {
  const at = instantFrom(value);
  return at ? at.getTime() : 0;
};

/**
 * True when this row belongs in the upcoming group.
 *
 * @param {Object} [tx]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isInboxUpcomingRow = (tx, now = new Date()) => {
  if (
    transactionWasLeft(tx) ||
    transactionIsListenerNoShow(tx) ||
    transactionIsTalkerNoShow(tx) ||
    isSupersededEditedBooking(tx) ||
    txPastNoShowDeadline(tx, now)
  ) {
    return false;
  }
  const state = processStateOf(tx);
  if (PAST_STATES.has(state) || CANCELLED_STATES.has(state)) {
    return false;
  }
  if (bookingHasEnded(tx, now)) {
    return false;
  }
  if (REQUESTED_STATES.has(state) || state === 'accepted' || state === 'in-session') {
    return true;
  }
  const { start } = bookingTimes(tx);
  return !!(start && start.getTime() >= now.getTime());
};

/**
 * True when this row is a cancelled / left session.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const isInboxCancelledRow = (tx, now = new Date()) => {
  if (isTalkerLateCancelCharge(tx)) {
    return true;
  }
  if (transactionWasLeft(tx) || transactionIsListenerNoShow(tx) || isSupersededEditedBooking(tx)) {
    return true;
  }
  if (transactionIsTalkerNoShow(tx)) {
    const startNow = tx?.attributes?.protectedData?.startNow === true;
    const outcome = tx?.attributes?.protectedData?.chargeOutcome;
    return startNow || outcome === 'not_charged';
  }
  if (txPastListenerNoShowDeadline(tx, now)) {
    return true;
  }
  const state = processStateOf(tx);
  return CANCELLED_STATES.has(state);
};

/**
 * True when this row is a completed / past session (not cancelled).
 *
 * @param {Object} [tx]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isInboxPastRow = (tx, now = new Date()) => {
  if (isInboxCancelledRow(tx, now) || isInboxUpcomingRow(tx, now)) {
    return false;
  }
  const state = processStateOf(tx);
  if (COMPLETED_STATES.has(state)) {
    return true;
  }
  if (COMPLETED_LAST_TRANSITIONS.includes(tx?.attributes?.lastTransition)) {
    return true;
  }
  return bookingHasEnded(tx, now);
};

/**
 * Sharetribe lastTransitions filter for the current Sort by key.
 * Past needs its own query so completed orders appear on page 1.
 *
 * @param {string} [sortKey]
 * @returns {string[]|null}
 */
export const inboxQueryLastTransitions = sortKey => {
  const key = normalizeInboxSort(sortKey);
  if (key === INBOX_SORT_PAST) {
    return COMPLETED_LAST_TRANSITIONS;
  }
  if (key === INBOX_SORT_UPCOMING) {
    return UPCOMING_LAST_TRANSITIONS;
  }
  return null;
};

const inboxTxId = tx => tx?.id?.uuid || (typeof tx?.id === 'string' ? tx.id : null);

/**
 * Put pinned upcoming bookings ahead of the recency page without dupes.
 *
 * @param {Array<Object>} [pinned]
 * @param {Array<Object>} [listed]
 * @returns {Array<Object>}
 */
export const mergeInboxTransactions = (pinned = [], listed = []) => {
  const seen = new Set();
  const out = [];
  [...(Array.isArray(pinned) ? pinned : []), ...(Array.isArray(listed) ? listed : [])].forEach(
    tx => {
      const id = inboxTxId(tx);
      if (!id || seen.has(id)) {
        return;
      }
      seen.add(id);
      out.push(tx);
    }
  );
  return out;
};

/**
 * Instant the right-side date should show for this sort.
 *
 * @param {Object} [tx]
 * @param {string} [sortKey]
 * @returns {Date|null}
 */
export const inboxSortDate = (tx, sortKey) => {
  const key = normalizeInboxSort(sortKey);
  if (key === INBOX_SORT_UPCOMING) {
    return (
      bookingTimes(tx).start ||
      instantFrom(tx?.attributes?.lastTransitionedAt) ||
      instantFrom(tx?.attributes?.createdAt)
    );
  }
  return instantFrom(tx?.attributes?.lastTransitionedAt) || instantFrom(tx?.attributes?.createdAt);
};

const startMs = tx => {
  const start = bookingTimes(tx).start;
  return start ? start.getTime() : Number.POSITIVE_INFINITY;
};

const compareNewest = (a, b) =>
  msOf(inboxSortDate(b, INBOX_SORT_ALL)) - msOf(inboxSortDate(a, INBOX_SORT_ALL));

/**
 * True when this row belongs in the current Sort by filter.
 *
 * @param {Object} [tx]
 * @param {string} [sortKey]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const matchesInboxSort = (tx, sortKey, now = new Date()) => {
  const key = normalizeInboxSort(sortKey);
  if (key === INBOX_SORT_UPCOMING) {
    return isInboxUpcomingRow(tx, now);
  }
  if (key === INBOX_SORT_PAST) {
    return isInboxPastRow(tx, now);
  }
  if (key === INBOX_SORT_CANCELLED) {
    return isInboxCancelledRow(tx, now);
  }
  return true;
};

/**
 * Client-side inbox order. Does not refetch.
 *
 * @param {Array<Object>} [transactions]
 * @param {string} [sortKey]
 * @param {Date} [now]
 * @returns {Array<Object>}
 */
export const sortInboxTransactions = (transactions = [], sortKey, now = new Date()) => {
  const key = normalizeInboxSort(sortKey);
  const rows = (Array.isArray(transactions) ? transactions : []).filter(tx =>
    matchesInboxSort(tx, key, now)
  );
  if (key === INBOX_SORT_UPCOMING) {
    return rows.sort((a, b) => {
      const byStart = startMs(a) - startMs(b);
      return byStart !== 0 ? byStart : compareNewest(a, b);
    });
  }
  return rows.sort((a, b) => compareNewest(a, b));
};
